import type { GuideRole } from "@/lib/guide";

/**
 * Download a Dinnobot conversation as a PDF — dependency-free.
 *
 * Opens a small, self-contained print window with the transcript and invokes
 * the browser's print dialog, where the user chooses "Save as PDF". This mirrors
 * how salary slips print (`window.print()` + print CSS) rather than pulling in a
 * PDF library. Returns `false` if the browser blocked the popup so the caller
 * can surface a hint.
 */

export interface TranscriptTurn {
  role: GuideRole;
  body: string;
}

/** Escape text before it goes into the print document (no HTML injection). */
function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Render the light Markdown Dinnobot uses (**bold**, line breaks) as safe HTML. */
function fmt(body: string): string {
  return esc(body)
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\r?\n/g, "<br>");
}

export function downloadTranscriptPdf(turns: TranscriptTurn[]): boolean {
  const win = window.open("", "_blank", "width=760,height=920");
  if (!win) return false;

  const generated = new Date().toLocaleString();
  const rows = turns
    .map(
      (t) => `
      <div class="turn ${t.role === "user" ? "user" : "guide"}">
        <div class="who">${t.role === "user" ? "You" : "Dinnobot"}</div>
        <div class="body">${fmt(t.body)}</div>
      </div>`,
    )
    .join("");

  win.document.write(`<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>Dinnobot conversation</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font: 13px/1.55 -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #0f172a; margin: 0; padding: 32px; background: #fff;
    }
    header { border-bottom: 2px solid #1e293b; padding-bottom: 12px; margin-bottom: 20px; }
    header h1 { font-size: 18px; margin: 0; }
    header p { margin: 4px 0 0; color: #64748b; font-size: 11px; }
    .turn { margin: 0 0 16px; page-break-inside: avoid; }
    .who { font-weight: 600; font-size: 11px; text-transform: uppercase;
           letter-spacing: .04em; color: #475569; margin-bottom: 3px; }
    .turn.user .who { color: #1d4ed8; }
    .body { white-space: normal; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <header>
    <h1>Dinnobot — how to use HRIS Workbench</h1>
    <p>Support conversation · generated ${esc(generated)}</p>
  </header>
  ${rows}
</body>
</html>`);
  win.document.close();
  win.focus();
  // Give the new document a tick to lay out before the print dialog.
  win.setTimeout(() => win.print(), 150);
  return true;
}
